// @coverage-include-exempt: behaviour asserted by
// tests/unit/lib/monetization/ad-wall.test.ts and
// tests/component/monetization/ad-wall-host.test.tsx; adding it to
// vitest.config.ts coverage.include is a human-gated path for agent commits.
/**
 * H7 ad wall — pure rules (scripts/research/monetization-experiments-2026-10/
 * DECISIONS.md, owner amendment 2026-10-29).
 *
 * A short, unskippable-but-graceful interstitial: one sponsored offer, a
 * countdown, then Continue. The `ad-wall-placement` PostHog multivariate flag
 * is the sole switch and decides WHERE it surfaces:
 *
 *  - `holdout`    permanent 20% holdout, never a wall (long-run harm check)
 *  - `control`    no wall
 *  - `feed`       on the jobs feed
 *  - `job_detail` on a job page, before the description is read
 *  - `apply`      in our tab right after an apply click (the employer tab has
 *                 already opened, so the apply itself is never blocked). Also
 *                 on a visit's landing page: a wall answering the visitor's
 *                 own click is not a load-time interstitial (H7/H8 v2).
 *
 * Graceful rules shared by every arm: at most once per visit (H8's
 * `repeat`/`heavy` intensities raise that to a capped few; a visit spans all
 * tabs and ends after 30 idle minutes), `feed`/`job_detail` never on the
 * landing page of a visit (keeps Google's intrusive-interstitial penalty off
 * search landings), never for Pro, never before the flag resolves.
 */
export const AD_WALL_FLAG = "ad-wall-placement";

/** Monetization surface the wall's offer is drawn for (settings-defaults). */
export const AD_WALL_SURFACE = "ad_wall";

/** Seconds before Continue unlocks in the `light` intensity (H7's wall). */
export const AD_WALL_SECONDS = 5;

/**
 * H8 ad-wall intensity (DECISIONS.md, amendment 2026-10-30). A second,
 * independent multivariate flag crossed with `ad-wall-placement`: placement
 * decides WHERE the wall opens, intensity decides HOW LONG and HOW OFTEN.
 * Holdout and control from H7 never see a wall whatever their intensity.
 */
export const AD_WALL_INTENSITY_FLAG = "ad-wall-intensity";

export const AD_WALL_INTENSITIES = [
	"light",
	"long",
	"repeat",
	"heavy",
] as const;
export type AdWallIntensity = (typeof AD_WALL_INTENSITIES)[number];

export type AdWallIntensityConfig = {
	/** Countdown before Continue unlocks. */
	seconds: number;
	/** In-site page views between walls after the first; `null` = once per visit. */
	repeatEvery: number | null;
	/** Most walls one visit can see (payload key `max_per_session`). */
	maxPerSession: number;
};

/**
 * Per-arm settings used when the flag's variant payload is absent or invalid.
 * The flag still picks the arm; its payload (`{"seconds", "repeat_every",
 * "max_per_session"}`) overrides these so they can be tuned without a deploy.
 */
export const AD_WALL_INTENSITY_DEFAULTS: Record<
	AdWallIntensity,
	AdWallIntensityConfig
> = {
	light: { seconds: AD_WALL_SECONDS, repeatEvery: null, maxPerSession: 1 },
	long: { seconds: 10, repeatEvery: null, maxPerSession: 1 },
	repeat: { seconds: AD_WALL_SECONDS, repeatEvery: 4, maxPerSession: 3 },
	heavy: { seconds: 10, repeatEvery: 4, maxPerSession: 3 },
};

/** Payload bounds: a typo in PostHog must never trap anyone or wall every page. */
const SECONDS_RANGE = [1, 30] as const;
const REPEAT_RANGE = [2, 50] as const;
const MAX_PER_SESSION_RANGE = [1, 5] as const;

/** Unknown, unresolved or off → `light` (H7's wall, once per session, 5 s). */
export function toAdWallIntensity(variantKey: unknown): AdWallIntensity {
	return typeof variantKey === "string" &&
		(AD_WALL_INTENSITIES as readonly string[]).includes(variantKey)
		? (variantKey as AdWallIntensity)
		: "light";
}

function intIn(
	value: unknown,
	[min, max]: readonly [number, number],
): number | undefined {
	return typeof value === "number" &&
		Number.isInteger(value) &&
		value >= min &&
		value <= max
		? value
		: undefined;
}

/**
 * The arm's settings: the flag payload's valid fields over the arm defaults.
 * PostHog may hand the payload as a JSON string or an object. `repeat_every:
 * null` turns repeats off; `max_per_session` is forced to 1 when repeats are
 * off so the two can never disagree.
 */
export function resolveIntensityConfig(
	intensity: AdWallIntensity,
	payload: unknown,
): AdWallIntensityConfig {
	const base = AD_WALL_INTENSITY_DEFAULTS[intensity];
	let raw: unknown = payload;
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw);
		} catch {
			return base;
		}
	}
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base;
	const p = raw as Record<string, unknown>;
	const seconds = intIn(p.seconds, SECONDS_RANGE) ?? base.seconds;
	const repeatEvery =
		p.repeat_every === null
			? null
			: (intIn(p.repeat_every, REPEAT_RANGE) ?? base.repeatEvery);
	const maxPerSession =
		repeatEvery === null
			? 1
			: (intIn(p.max_per_session, MAX_PER_SESSION_RANGE) ??
				Math.max(base.maxPerSession, 1));
	return { seconds, repeatEvery, maxPerSession };
}

export const AD_WALL_ARMS = [
	"holdout",
	"control",
	"feed",
	"job_detail",
	"apply",
] as const;
export type AdWallArm = (typeof AD_WALL_ARMS)[number];

/** Page that hosts an `AdWallHost`. */
export type AdWallPage = "feed" | "job_detail";

/** What can open the wall on a hosting page. */
export type AdWallTrigger = "mount" | "apply";

/** Unknown, unresolved or off → `control` (no wall). */
export function toAdWallArm(variantKey: unknown): AdWallArm {
	return typeof variantKey === "string" &&
		(AD_WALL_ARMS as readonly string[]).includes(variantKey)
		? (variantKey as AdWallArm)
		: "control";
}

/** True when this arm opens the wall for this page and trigger. */
export function wallOpensFor(
	arm: AdWallArm,
	page: AdWallPage,
	trigger: AdWallTrigger,
): boolean {
	if (trigger === "apply") return arm === "apply";
	return arm === page;
}

/**
 * True when the current page is the first page of the visit (a landing).
 *
 * `navigationUrl` is the URL the browser hard-loaded
 * (`performance.getEntriesByType("navigation")[0].name`). After a client-side
 * navigation `href` differs from it, and `document.referrer` still names the
 * original external referrer, so the referrer alone would call every page of
 * the visit a landing.
 */
export function isLandingPage({
	href,
	navigationUrl,
	referrer,
	origin,
}: {
	href: string;
	navigationUrl: string | null;
	referrer: string;
	origin: string;
}): boolean {
	if (navigationUrl && navigationUrl !== href) return false;
	if (!referrer) return true;
	try {
		return new URL(referrer).origin !== origin;
	} catch {
		return true;
	}
}

/**
 * The wall's cap lives in a per-VISIT store (H7/H8 v2, owner approval
 * 2026-10-30): `localStorage`, so every tab of one visit shares it, expiring
 * after 30 minutes without a page view (the standard web-analytics session).
 * Per-tab `sessionStorage` let a new tab reset the cap. When `localStorage`
 * throws (privacy mode, blocked site data), the store falls back to
 * `sessionStorage`; when both throw it reads as unknown. Never a cookie: the
 * job pages are edge-cached and must not emit Set-Cookie.
 */
export const VISIT_IDLE_MS = 30 * 60 * 1000;
const VISIT_KEY = "ld:ad-wall:visit";
/** Pre-v2 per-tab keys (sessionStorage), read once to seed a fresh visit. */
const LEGACY_SHOWN_KEY = "ld:ad-wall:shown";
const LEGACY_ELIGIBLE_KEY = "ld:ad-wall:eligible";

type VisitState = {
	/** Walls shown this visit. */
	shown: number;
	/** In-site page views since the last wall. */
	since: number;
	/** `ad_wall_eligible` already sent this visit. */
	eligible: boolean;
};

function count(v: unknown): number {
	const n = typeof v === "string" ? Number.parseInt(v, 10) : v;
	return typeof n === "number" && Number.isInteger(n) && n >= 0 ? n : 0;
}

/** The first storage that both reads and writes, or null. */
function visitStorage(): Storage | null {
	for (const pick of [
		() => globalThis.localStorage,
		() => globalThis.sessionStorage,
	]) {
		try {
			const store = pick();
			if (!store) continue;
			const raw = store.getItem(VISIT_KEY);
			store.setItem(VISIT_KEY, raw ?? "");
			return store;
		} catch {
			/* blocked: try the next store */
		}
	}
	return null;
}

/**
 * A new visit, seeded from this tab's pre-v2 per-tab cap when present. The
 * legacy keys are consumed so they cannot re-seed every later visit.
 */
function freshVisit(): VisitState {
	try {
		const tab = globalThis.sessionStorage;
		const visit = {
			shown: count(tab.getItem(LEGACY_SHOWN_KEY)),
			since: 0,
			eligible: tab.getItem(LEGACY_ELIGIBLE_KEY) === "1",
		};
		tab.removeItem(LEGACY_SHOWN_KEY);
		tab.removeItem(LEGACY_ELIGIBLE_KEY);
		return visit;
	} catch {
		return { shown: 0, since: 0, eligible: false };
	}
}

/**
 * Reads the visit (starting a new one when absent or idle too long), lets
 * `update` change it, stamps the activity time and writes it back. Returns
 * null when no storage works.
 */
function withVisit<T>(update: (v: VisitState) => T): T | null {
	const store = visitStorage();
	if (!store) return null;
	const now = Date.now();
	let visit: VisitState | null = null;
	try {
		const raw = store.getItem(VISIT_KEY);
		const parsed: unknown = raw ? JSON.parse(raw) : null;
		if (parsed && typeof parsed === "object") {
			const p = parsed as Record<string, unknown>;
			const idle = now - (typeof p.last === "number" ? p.last : 0);
			if (idle >= 0 && idle < VISIT_IDLE_MS) {
				visit = {
					shown: count(p.shown),
					since: count(p.since),
					eligible: p.eligible === true,
				};
			}
		}
	} catch {
		/* corrupt value: a fresh visit */
	}
	visit ??= freshVisit();
	const result = update(visit);
	try {
		store.setItem(VISIT_KEY, JSON.stringify({ ...visit, last: now }));
	} catch {
		/* write failed: the in-memory guard in the host still holds this page */
	}
	return result;
}

/**
 * Storage unreadable counts as already shown: a viewer whose cap we cannot
 * remember must never get a wall on every page.
 */
export function wallShownThisVisit(): boolean {
	return wallsShownThisVisit() !== 0;
}

/** Walls shown this visit; `null` when no storage works. */
export function wallsShownThisVisit(): number | null {
	return withVisit((v) => v.shown);
}

/** Records one more wall and restarts the page-view count toward the next. */
export function markWallShown(): void {
	withVisit((v) => {
		v.shown += 1;
		v.since = 0;
	});
}

/**
 * Counts one in-site page view after the visit's first wall (H8 `repeat`
 * and `heavy`). Returns the views since the last wall, or `null` when storage
 * is unreadable.
 */
export function countPageViewSinceWall(): number | null {
	return withVisit((v) => {
		if (v.shown === 0) return 0;
		v.since += 1;
		return v.since;
	});
}

/**
 * True when this visit is due a wall under `config`: the first one is due
 * on any eligible page view; a repeat is due once `repeatEvery` page views
 * have passed since the last wall and the visit is under its cap. Unknown
 * storage (`shown === null`) is never due.
 */
export function wallDue(
	config: AdWallIntensityConfig,
	shown: number | null,
	viewsSinceWall: number | null,
): boolean {
	if (shown === null) return false;
	if (shown === 0) return true;
	if (config.repeatEvery === null || viewsSinceWall === null) return false;
	return shown < config.maxPerSession && viewsSinceWall >= config.repeatEvery;
}

/**
 * Claims the once-per-visit `ad_wall_eligible` event. Returns true the first
 * time in a visit; storage unreadable returns false (no event beats a
 * per-page flood in the denominator).
 */
export function claimEligibleThisVisit(): boolean {
	return (
		withVisit((v) => {
			if (v.eligible) return false;
			v.eligible = true;
			return true;
		}) ?? false
	);
}
